import { useEffect, useState } from 'react';
import { WhatsApp } from '@mui/icons-material';
import { Bot, Twitter, Instagram } from 'lucide-react';
import { AiOutlineDiscord } from "react-icons/ai";

import { useTranslation } from 'react-i18next';

import LocalizedLink from './LocalizedLink';
import StoreBadges from './StoreBadges';

const FOOTER_GEO_COUNTRY_KEY = 'footerGeoCountry';
const FOOTER_GEO_TS_KEY = 'footerGeoTimestamp';
const FOOTER_GEO_TTL_MS = 6 * 30 * 24 * 60 * 60 * 1000;

export default function Footer() {
  const { t } = useTranslation('footer');
  const initialIsIndian = typeof window !== 'undefined' && (window as { __isIndianLocale__?: boolean }).__isIndianLocale__ === true;
  const [isIndian, setIsIndian] = useState(initialIsIndian);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const cachedCountry = localStorage.getItem(FOOTER_GEO_COUNTRY_KEY);
    const cachedTs = Number(localStorage.getItem(FOOTER_GEO_TS_KEY) || 0);
    const now = Date.now();
    if (cachedCountry && cachedTs && now - cachedTs < FOOTER_GEO_TTL_MS) {
      const cachedIsIndian = cachedCountry.toUpperCase() === 'IN';
      if (cachedIsIndian !== isIndian) setIsIndian(cachedIsIndian);
      return undefined;
    }

    const run = async () => {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 3000);
      try {
        const r = await fetch('https://ipapi.co/json/', { signal: controller.signal });
        if (r.ok) {
          const j = await r.json();
          const code = String(j.country_code || j.country || '').toUpperCase();
          if (code) {
            localStorage.setItem(FOOTER_GEO_COUNTRY_KEY, code);
            localStorage.setItem(FOOTER_GEO_TS_KEY, String(Date.now()));
            const newIsIndian = code === 'IN';
            setIsIndian((prev) => (prev === newIsIndian ? prev : newIsIndian));
            return;
          }
        }
      } catch {
        void 0;
      } finally {
        clearTimeout(timeoutId);
      }

      try {
        const r = await fetch('https://geolocation-db.com/json/');
        if (!r.ok) return;
        const j = await r.json();
        const code = String(j.country_code || '').toUpperCase();
        if (!code) return;
        localStorage.setItem(FOOTER_GEO_COUNTRY_KEY, code);
        localStorage.setItem(FOOTER_GEO_TS_KEY, String(Date.now()));
        const newIsIndian = code === 'IN';
        setIsIndian((prev) => (prev === newIsIndian ? prev : newIsIndian));
      } catch {
        void 0;
      }
    };

    let timeoutHandle: number | undefined;
    let idleHandle: number | undefined;
    if ('requestIdleCallback' in window) {
      idleHandle = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback?.(run, { timeout: 2000 });
    } else {
      timeoutHandle = window.setTimeout(run, 1200);
    }

    return () => {
      if (idleHandle && 'cancelIdleCallback' in window) {
        (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback?.(idleHandle);
      }
      if (timeoutHandle) clearTimeout(timeoutHandle);
    };
  }, [isIndian]);

  const year = new Date().getFullYear();
  const copyrightText = isIndian
    ? `© Sarvalok ${year}`
    : `© Sarvalok Inc. ${year} All rights reserved.`;

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <footer className="text-gray-600 border-t border-gray-200">
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-8">
          {/* Brand Section */}
          <div>
               <div className="flex items-center">
                         <button className="p-2 hover:bg-black/5 rounded-md transition-colors">
                           <img src='/logo.png' alt="Sarvalok" style={{width:"30px",minWidth:"30px"}} />
                         </button>
                         {/* Previous logo text style (for quick rollback): uppercase, 15px, fontWeight 800, marginLeft -6px, marginTop 10px */}
                         {/* <LocalizedLink to="/" style={{ fontWeight: 800,marginLeft:"-6px",marginTop:"10px" }} className=" font-semibold text-[15px]">
                           SARVALOK
                         </LocalizedLink> */}
                         <LocalizedLink
                           to="/"
                           style={{ fontFamily: "'General Sans', 'Inter', 'Segoe UI', -apple-system, sans-serif", fontWeight: 600, marginLeft: "-6px", marginTop: "10px" }}
                           className="text-[15px] leading-none whitespace-nowrap"
                         >
                           Sarvalok
                         </LocalizedLink>
                       
                       </div>
            <p className="text-gray-600 mx-4">
              {t('footer.tagline')}
            </p>

            <img src='/gdpr-footer.svg' alt="Sarvalok" className='ml-4 mt-4' />
          </div>

          {/* Product Section */}
          <div>
            <h3 className="font-semibold mb-4 text-gray-900">{t('footer.product')}</h3>
            <ul className="space-y-3">
              <li>
              <LocalizedLink to="/features" className="hover:text-blue-600 transition-colors">{t('footer.features')}</LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/pricing-policy" className="hover:text-blue-600 transition-colors">
                  {t('footer.pricing_policy')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/enterprise" className="hover:text-blue-600 transition-colors">
                  {t('footer.enterprise')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/security" className="hover:text-blue-600 transition-colors">
                  {t('footer.security')}
                </LocalizedLink>
              </li>
            </ul>
          </div>

          {/* Resources Section */}
          <div>
            <h3 className="font-semibold mb-4 text-gray-900">{t('footer.resources')}</h3>
            <ul className="space-y-3" style={{width:"max-content"}}>
              <li>
                <LocalizedLink to="/documentation" className="hover:text-blue-600 transition-colors">
                  {t('footer.documentation')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/blog" className="hover:text-blue-600 transition-colors">
                  {t('footer.blog', 'Blog')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/use-cases" className="hover:text-blue-600 transition-colors">
                  {t('footer.use_cases')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/proof" className="hover:text-blue-600 transition-colors">
                  {t('footer.accuracy')}
                </LocalizedLink>
              </li>
            </ul>
          </div>

          {/* Company Section */}
          <div>
            <h3 className="font-semibold mb-4 text-gray-900">{t('footer.company')}</h3>
            <ul className="space-y-3">
              <li>
                <LocalizedLink to="/about-us" className="hover:text-blue-600 transition-colors">
                  {t('footer.about_us')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/privacy-policy" className="hover:text-blue-600 transition-colors">
                  {t('footer.privacy_policy')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/contact-us" className="hover:text-blue-600 transition-colors">
                  {t('footer.contact')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/terms-condition" className="hover:text-blue-600 transition-colors">
                  {t('footer.terms_conditions')}
                </LocalizedLink>
              </li>
            </ul>
          </div>

          {/* Decorative Plant Image */}
          <div className='hidden xl:block'>
            <img style={{width:"200px"}} src="/bottom-plant.png" alt="Decorative plant"/>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="border-t border-gray-200 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            {/* Left Side - Copyright */}
            <p className="text-gray-600">
              {copyrightText}
            </p>

            {/* Centre - store badges. Side by side here rather than stacked in
                the Resources column: the two official badges have different
                aspect ratios (Apple 2.99:1, Play 3.36:1), so at equal height
                the Play badge is ~12% wider. Stacked and left-aligned that
                reads as a size mismatch; side by side it does not. */}
            <StoreBadges className="justify-center mt-6 md:mt-0" height={40} />
            
            {/* Right Side - Social Media Icons and Back to Top */}
            <div className="flex flex-col items-center space-y-4 mt-4 md:mt-0">
              {/* Social Media Icons */}
              <div className="flex space-x-6 items-center">
                <a
                  href="https://twitter.com/thesarvalok"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a
                  href="https://www.instagram.com/thesarvalok/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://api.whatsapp.com/send/?phone=918125831103&text=Hello%2C+I+have+a+question+about+Sarvalok.+Can+you+please+help+me%3F&type=phone_number&app_absent=0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <WhatsApp className="w-5 h-5" />
                </a>
                <a
                  href="https://discordapp.com/channels/1417103103949275220/1417104567471964200"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <AiOutlineDiscord className="w-6 h-6" />
                </a>
              </div>
              
              {/* Back to Top Button - positioned below social icons */}
              <button
                onClick={scrollToTop}
                className="flex items-center gap-2 text-gray-400 hover:text-gray-300 transition-colors group bg-transparent border-none cursor-pointer p-0 opacity-80"
                aria-label={t('footer.back_to_top')}
              >
                <span className="text-sm">{t('footer.back_to_top')}</span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="16" 
                  height="17" 
                  fill="none" 
                  viewBox="0 0 16 17"
                  className="transition-all duration-200 group-hover:stroke-gray-400"
                >
                  <circle 
                    cx="8" 
                    cy="8.846" 
                    r="6" 
                    stroke="#A6AAB5" 
                    strokeWidth="1.2" 
                    fill="none"
                    className="transition-all duration-200 group-hover:stroke-gray-400"
                  /> 
                  <path 
                    stroke="#A6AAB5" 
                    strokeLinecap="square" 
                    strokeWidth="1.2" 
                    d="M8 10.846v-3.2m1.6 1.6-1.6-1.6-1.6 1.6"
                    className="transition-all duration-200 group-hover:stroke-gray-400"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
